import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface Family {
  id: number;
  name: string;
  description: string;
  created_by: number;
  people: Person[];
  created_at: string;
  updated_at: string;
}

interface Person {
  id: number;
  name: string;
  relationship: string;
  is_deceased: boolean;
  family_id: number;
  created_at: string;
  updated_at: string;
}

const Families: React.FC = () => {
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFamily, setEditingFamily] = useState<Family | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'tile'>('table');
  const [selectedFamilies, setSelectedFamilies] = useState<number[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/families');
      setFamilies(response.data.families);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch families');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (family?: Family) => {
    if (family) {
      setEditingFamily(family);
      setFormData({
        name: family.name,
        description: family.description || '',
      });
    } else {
      setEditingFamily(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFamily(null);
    setFormData({
      name: '',
      description: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setSuccess('');

      if (editingFamily) {
        await api.put(`/api/families/${editingFamily.id}`, formData);
        setSuccess('Family updated successfully');
      } else {
        await api.post('/api/families', formData);
        setSuccess('Family created successfully');
      }

      handleCloseDialog();
      fetchFamilies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save family');
    }
  };

  const handleDelete = async (familyId: number) => {
    if (!window.confirm('Are you sure you want to delete this family?')) {
      return;
    }

    try {
      setError('');
      await api.delete(`/api/families/${familyId}`);
      setSuccess('Family deleted successfully');
      fetchFamilies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete family');
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedFamilies(families.map(f => f.id));
    } else {
      setSelectedFamilies([]);
    }
  };

  const handleSelectOne = (familyId: number) => {
    setSelectedFamilies(prev => {
      if (prev.includes(familyId)) {
        return prev.filter(id => id !== familyId);
      } else {
        return [...prev, familyId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedFamilies.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedFamilies.length} selected families?`)) {
      return;
    }

    try {
      setError('');
      const deletePromises = selectedFamilies.map(familyId => 
        api.delete(`/api/families/${familyId}`)
      );
      
      await Promise.all(deletePromises);
      setSuccess(`Successfully deleted ${selectedFamilies.length} families`);
      setSelectedFamilies([]);
      fetchFamilies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete some families');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            Families
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            {selectedFamilies.length > 0 && viewMode === 'table' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedFamilies.length})
              </Button>
            )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="tile">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchFamilies}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Family
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Organize family members, track relationships, and manage family finances.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {viewMode === 'table' ? (
        <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
          <Table size="small" sx={FIT_TABLE_SX}>
            <FitColgroup
              cols={buildFitColgroup([
                { key: 'select', pct: 3 },
                { key: 'name', pct: 18 },
                { key: 'description' },
                { key: 'members', pct: 25 },
                { key: 'created', pct: 12 },
                { key: 'actions', pct: 10 },
              ])}
            />
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedFamilies.length === families.length && families.length > 0}
                    indeterminate={selectedFamilies.length > 0 && selectedFamilies.length < families.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Family Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {families.map((family) => (
                <TableRow 
                  key={family.id} 
                  hover
                  selected={selectedFamilies.includes(family.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedFamilies.includes(family.id)}
                      onChange={() => handleSelectOne(family.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="medium" sx={fitTruncateSx} title={family.name}>
                      {family.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={fitTruncateSx}
                      title={family.description || ''}
                    >
                      {family.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {family.people && family.people.length > 0 ? (
                        family.people.map((person) => (
                          <Chip
                            key={person.id}
                            label={person.name}
                            size="small"
                            icon={<PersonIcon />}
                            sx={{ 
                              opacity: person.is_deceased ? 0.5 : 1,
                              fontSize: '0.75rem',
                              height: '24px'
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No members
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(family.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" className="fit-table-no-clip">
                    <Tooltip title="Edit Family">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(family)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Family">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(family.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {families.map((family) => (
            <Box sx={{ flex: '1 1 calc(33.333% - 24px)', minWidth: '300px' }} key={family.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {family.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {family.description || 'No description provided'}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Family Members ({family.people.length}):
                    </Typography>
                    {family.people.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {family.people.map((person) => (
                          <Chip
                            key={person.id}
                            icon={<PersonIcon />}
                            label={person.name}
                            size="small"
                            variant={person.is_deceased ? 'outlined' : 'filled'}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No members added yet
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(family)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(family.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {families.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No families created yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start by creating your first family to organize family members and relationships.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create First Family
          </Button>
        </Box>
      )}

      {/* Add/Edit Family Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFamily ? 'Edit Family' : 'Add New Family'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="normal"
            label="Family Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <TextField
            margin="normal"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingFamily ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export { Families };
